import { useEffect, useMemo, useState, useCallback } from "react";
import {
  adminGetCrmContacts, adminGetCrmSubmissions, adminUpdateCrmContact,
  formatApiErrorDetail,
} from "@/lib/api";
import {
  Search, Download, Copy, Mail, X, Users, Inbox, Check, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["New", "Contacted", "Qualified", "Won", "Lost"];

const STATUS_STYLE = {
  New: "border-[#A6633C] text-[#A6633C]",
  Contacted: "border-[#515E4F] text-[#515E4F]",
  Qualified: "border-[#1F1B16] text-[#1F1B16]",
  Won: "bg-[#515E4F] text-[#F6F2EC] border-[#515E4F]",
  Lost: "border-[rgba(28,27,25,0.3)] text-[#9A938A]",
};

const TYPE_OPTIONS = [
  { value: "", label: "All sources" },
  { value: "brief", label: "Brief Request" },
  { value: "packet", label: "Operating Room" },
  { value: "partner", label: "Referral Partner" },
  { value: "production", label: "Executive Production" },
  { value: "table_intro", label: "Table Intro" },
  { value: "scorecard", label: "Readiness Scorecard" },
  { value: "download", label: "Resource Download" },
  { value: "event", label: "Event RSVP" },
  { value: "purchase", label: "Purchase" },
  { value: "member", label: "Hub Member" },
];

const SOURCE_TAG = "cv-sans uppercase tracking-[0.1em] text-[0.58rem] px-2 py-0.5 rounded-full border border-[rgba(28,27,25,0.18)] text-[#1F1B16]/70";

function fmtDate(iso) {
  if (!iso) return "--";
  const d = new Date(iso);
  if (isNaN(d)) return String(iso).slice(0, 10);
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function csvEscape(v) {
  const s = v == null ? "" : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function downloadCsv(filename, headers, rows) {
  const head = headers.map((h) => csvEscape(h.label)).join(",");
  const body = rows.map((r) => headers.map((h) => csvEscape(h.get(r))).join(",")).join("\n");
  const csv = `${head}\n${body}`;
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function AdminCRM() {
  const [tab, setTab] = useState("contacts");
  const [search, setSearch] = useState("");
  const [type, setType] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [loading, setLoading] = useState(true);
  const [contacts, setContacts] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async (activeTab, params) => {
    setLoading(true);
    try {
      if (activeTab === "contacts") {
        const d = await adminGetCrmContacts(params);
        setContacts(d.contacts || []);
      } else {
        const d = await adminGetCrmSubmissions(params);
        setSubmissions(d.submissions || []);
      }
    } catch (e) {
      // admin route already guards; surface gentle error
      toast.error("Could not load CRM data.");
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounced fetch on any filter / tab change
  useEffect(() => {
    const params = {};
    if (search.trim()) params.q = search.trim();
    if (type) params.type = type;
    if (start) params.start = start;
    if (end) params.end = end;
    const t = setTimeout(() => load(tab, params), 300);
    return () => clearTimeout(t);
  }, [tab, search, type, start, end, load]);

  const activeRows = tab === "contacts" ? contacts : submissions;

  const exportCsv = () => {
    if (!activeRows.length) {
      toast.error("Nothing to export with the current filters.");
      return;
    }
    if (tab === "contacts") {
      downloadCsv("coppervine-contacts.csv", [
        { label: "Name", get: (r) => r.name },
        { label: "Email", get: (r) => r.email },
        { label: "Company", get: (r) => r.company },
        { label: "Phone", get: (r) => r.phone },
        { label: "Status", get: (r) => r.status },
        { label: "Notes", get: (r) => r.notes },
        { label: "Last contacted", get: (r) => r.last_contacted },
        { label: "Interactions", get: (r) => r.count },
        { label: "Sources", get: (r) => (r.sources || []).join(" | ") },
        { label: "First seen", get: (r) => (r.first_seen || "").slice(0, 10) },
        { label: "Last seen", get: (r) => (r.last_seen || "").slice(0, 10) },
      ], contacts);
    } else {
      downloadCsv("coppervine-submissions.csv", [
        { label: "Date", get: (r) => (r.created_at || "").slice(0, 10) },
        { label: "Source", get: (r) => r.source },
        { label: "Name", get: (r) => r.name },
        { label: "Email", get: (r) => r.email },
        { label: "Company", get: (r) => r.company },
        { label: "Phone", get: (r) => r.phone },
        { label: "Detail", get: (r) => r.detail },
        { label: "Status", get: (r) => r.status || "" },
        { label: "Amount", get: (r) => (r.amount != null ? r.amount : "") },
        { label: "UTM source", get: (r) => (r.utm || {}).source || "" },
        { label: "UTM campaign", get: (r) => (r.utm || {}).campaign || "" },
      ], submissions);
    }
    toast.success("CSV exported.");
  };

  return (
    <div data-testid="admin-crm">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <p className="cv-kicker">Pipeline</p>
          <h1 className="cv-display text-3xl mt-4">Every interaction, in one place.</h1>
          <p className="cv-body text-sm text-[#1F1B16]/65 mt-3 max-w-xl">
            A single view of every form, RSVP, download, and purchase across the site, with a
            lightweight pipeline so you can pull sales actions without leaving the panel.
          </p>
        </div>
        <button
          data-testid="crm-export-csv"
          onClick={exportCsv}
          className="cv-btn-secondary inline-flex items-center gap-2 whitespace-nowrap self-start"
        >
          <Download size={15} /> Export CSV
        </button>
      </div>

      {/* Tabs */}
      <div className="mt-8 flex items-center gap-1 border-b border-[rgba(28,27,25,0.14)]">
        <TabButton active={tab === "contacts"} onClick={() => { setTab("contacts"); setSelected(null); }}
          testid="crm-tab-contacts" icon={<Users size={14} />} label="Contacts" />
        <TabButton active={tab === "submissions"} onClick={() => { setTab("submissions"); setSelected(null); }}
          testid="crm-tab-submissions" icon={<Inbox size={14} />} label="Submissions" />
      </div>

      {/* Filters */}
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-12 gap-3 items-end">
        <div className="sm:col-span-5 relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9A938A]" />
          <input
            data-testid="crm-search"
            className="cv-input pl-9"
            placeholder="Search name or email"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="sm:col-span-3">
          <select data-testid="crm-type-filter" className="cv-input" value={type}
            onChange={(e) => setType(e.target.value)}>
            {TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className="cv-sans uppercase tracking-[0.12em] text-[0.6rem] text-[#1F1B16]/55">From</label>
          <input data-testid="crm-date-from" type="date" className="cv-input mt-1" value={start}
            onChange={(e) => setStart(e.target.value)} />
        </div>
        <div className="sm:col-span-2">
          <label className="cv-sans uppercase tracking-[0.12em] text-[0.6rem] text-[#1F1B16]/55">To</label>
          <input data-testid="crm-date-to" type="date" className="cv-input mt-1" value={end}
            onChange={(e) => setEnd(e.target.value)} />
        </div>
      </div>

      {/* Count line */}
      <p data-testid="crm-result-count" className="cv-body text-xs text-[#9A938A] mt-4">
        {loading ? "Loading..." : `${activeRows.length} ${tab === "contacts" ? "contact" : "submission"}${activeRows.length === 1 ? "" : "s"}`}
      </p>

      {/* List */}
      <div className="mt-3">
        {loading ? (
          <ListSkeleton />
        ) : activeRows.length === 0 ? (
          <p data-testid="crm-empty" className="cv-body text-sm text-[#9A938A] border-l-2 border-[rgba(28,27,25,0.2)] pl-3">
            No {tab} match the current filters.
          </p>
        ) : tab === "contacts" ? (
          <ContactList contacts={contacts} onOpen={setSelected} />
        ) : (
          <SubmissionList submissions={submissions} />
        )}
      </div>

      {/* Contact detail drawer */}
      {selected && (
        <ContactDrawer
          contact={selected}
          onClose={() => setSelected(null)}
          onSaved={(updated) => {
            setContacts((prev) => prev.map((c) =>
              c.email.toLowerCase() === updated.email.toLowerCase()
                ? { ...c, status: updated.status, notes: updated.notes, last_contacted: updated.last_contacted }
                : c));
            setSelected((s) => s ? { ...s, ...updated } : s);
          }}
        />
      )}
    </div>
  );
}

function TabButton({ active, onClick, testid, icon, label }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`inline-flex items-center gap-2 px-4 py-2.5 cv-sans uppercase tracking-[0.12em] text-xs border-b-2 -mb-px transition-colors ${
        active ? "border-[#A6633C] text-[#1F1B16]" : "border-transparent text-[#1F1B16]/50 hover:text-[#1F1B16]"
      }`}
    >
      {icon} {label}
    </button>
  );
}

function CopyEmail({ email, testid }) {
  const [copied, setCopied] = useState(false);
  if (!email) return null;
  return (
    <button
      data-testid={testid}
      onClick={(e) => {
        e.stopPropagation();
        navigator.clipboard?.writeText(email);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
        toast.success("Email copied.");
      }}
      title="Copy email"
      className="text-[#9A938A] hover:text-[#A6633C] transition-colors p-1"
    >
      {copied ? <Check size={14} /> : <Copy size={14} />}
    </button>
  );
}

function MailTo({ email, testid }) {
  if (!email) return null;
  return (
    <a
      data-testid={testid}
      href={`mailto:${email}`}
      onClick={(e) => e.stopPropagation()}
      title="Send email"
      className="text-[#9A938A] hover:text-[#515E4F] transition-colors p-1 inline-flex"
    >
      <Mail size={14} />
    </a>
  );
}

function ContactList({ contacts, onOpen }) {
  return (
    <div className="divide-y divide-[rgba(28,27,25,0.12)] border-t border-[rgba(28,27,25,0.12)]">
      {contacts.map((c) => (
        <div
          key={c.email}
          data-testid="crm-contact-row"
          className="py-4 grid grid-cols-12 gap-3 items-center group hover:bg-[rgba(28,27,25,0.02)] transition-colors cursor-pointer"
          onClick={() => onOpen(c)}
        >
          <div className="col-span-12 sm:col-span-4 min-w-0">
            <p className="cv-h2 text-sm truncate">{c.name || "(no name)"}</p>
            <p className="cv-body text-xs text-[#9A938A] mt-0.5 truncate">{c.email}</p>
          </div>
          <div className="col-span-6 sm:col-span-3 flex flex-wrap gap-1.5">
            {(c.sources || []).slice(0, 3).map((s) => <span key={s} className={SOURCE_TAG}>{s}</span>)}
            {(c.sources || []).length > 3 && (
              <span className={SOURCE_TAG}>+{c.sources.length - 3}</span>
            )}
          </div>
          <div className="col-span-3 sm:col-span-2">
            <p className="cv-body text-xs text-[#1F1B16]/60">{c.count} interaction{c.count === 1 ? "" : "s"}</p>
            <p className="cv-body text-[0.68rem] text-[#9A938A] mt-0.5">Last {fmtDate(c.last_seen)}</p>
          </div>
          <div className="col-span-6 sm:col-span-2">
            <span className={`cv-sans uppercase tracking-[0.1em] text-[0.6rem] px-2.5 py-1 rounded-full border ${STATUS_STYLE[c.status] || STATUS_STYLE.New}`}>
              {c.status}
            </span>
          </div>
          <div className="col-span-3 sm:col-span-1 flex justify-end items-center gap-1">
            <CopyEmail email={c.email} testid="crm-contact-copy" />
            <MailTo email={c.email} testid="crm-contact-mailto" />
            <ChevronRight size={16} className="text-[#9A938A] group-hover:text-[#A6633C] transition-colors" />
          </div>
        </div>
      ))}
    </div>
  );
}

function SubmissionList({ submissions }) {
  return (
    <div className="divide-y divide-[rgba(28,27,25,0.12)] border-t border-[rgba(28,27,25,0.12)]">
      {submissions.map((s) => (
        <div
          key={s.id}
          data-testid="crm-submission-row"
          className="py-4 grid grid-cols-12 gap-3 items-center"
        >
          <div className="col-span-6 sm:col-span-2">
            <p className="cv-body text-xs text-[#1F1B16]/70">{fmtDate(s.created_at)}</p>
            <span className={`${SOURCE_TAG} mt-1 inline-block`}>{s.source}</span>
          </div>
          <div className="col-span-6 sm:col-span-3 min-w-0">
            <p className="cv-h2 text-sm truncate">{s.name || "(no name)"}</p>
            <p className="cv-body text-xs text-[#9A938A] mt-0.5 truncate">{s.email || "--"}</p>
          </div>
          <div className="col-span-12 sm:col-span-5 min-w-0">
            <p className="cv-body text-sm text-[#1F1B16]/80 truncate">{s.detail || "--"}</p>
            <div className="flex items-center gap-3 mt-0.5">
              {s.status && (
                <span className="cv-sans uppercase tracking-[0.1em] text-[0.58rem] text-[#9A938A]">{s.status}</span>
              )}
              {s.amount != null && s.amount > 0 && (
                <span className="cv-body text-[0.68rem] text-[#515E4F]">${s.amount}</span>
              )}
              {(s.utm || {}).source && (
                <span className="cv-body text-[0.66rem] text-[#9A938A]">via {(s.utm).source}</span>
              )}
            </div>
          </div>
          <div className="col-span-12 sm:col-span-2 flex justify-start sm:justify-end items-center gap-1">
            <CopyEmail email={s.email} testid="crm-submission-copy" />
            <MailTo email={s.email} testid="crm-submission-mailto" />
          </div>
        </div>
      ))}
    </div>
  );
}

function ContactDrawer({ contact, onClose, onSaved }) {
  const [status, setStatus] = useState(contact.status || "New");
  const [notes, setNotes] = useState(contact.notes || "");
  const [lastContacted, setLastContacted] = useState(contact.last_contacted || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const res = await adminUpdateCrmContact({
        email: contact.email, status, notes, last_contacted: lastContacted,
      });
      toast.success("Contact updated.");
      onSaved({ email: contact.email, status, notes, last_contacted: lastContacted, ...(res.contact || {}) });
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not save contact.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex justify-end" data-testid="crm-contact-drawer">
      {/* Backdrop */}
      <button
        aria-label="Close"
        data-testid="crm-drawer-backdrop"
        onClick={onClose}
        className="absolute inset-0 bg-[#1F1B16]/30 backdrop-blur-[2px]"
      />
      {/* Panel */}
      <div className="relative w-full max-w-md h-full bg-[#FBF8F2] border-l border-[rgba(28,27,25,0.16)] shadow-xl overflow-y-auto">
        <div className="cv-hairline-copper" />
        <div className="p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="cv-kicker">Contact</p>
              <h2 className="cv-h2 text-lg mt-2 truncate">{contact.name || "(no name)"}</h2>
              <p className="cv-body text-sm text-[#1F1B16]/70 mt-1 break-all">{contact.email}</p>
              {(contact.company || contact.phone) && (
                <p className="cv-body text-xs text-[#9A938A] mt-1">
                  {[contact.company, contact.phone].filter(Boolean).join(" · ")}
                </p>
              )}
            </div>
            <button data-testid="crm-drawer-close" onClick={onClose}
              className="text-[#9A938A] hover:text-[#1F1B16] transition-colors shrink-0">
              <X size={18} />
            </button>
          </div>

          <div className="flex items-center gap-2 mt-4">
            <a data-testid="crm-drawer-mailto" href={`mailto:${contact.email}`}
              className="cv-btn-secondary inline-flex items-center gap-2 text-xs py-2">
              <Mail size={13} /> Email
            </a>
            <button data-testid="crm-drawer-copy"
              onClick={() => { navigator.clipboard?.writeText(contact.email); toast.success("Email copied."); }}
              className="cv-btn-secondary inline-flex items-center gap-2 text-xs py-2">
              <Copy size={13} /> Copy email
            </button>
          </div>

          {/* Pipeline editor */}
          <div className="mt-7 space-y-4 border border-[rgba(28,27,25,0.16)] rounded-[14px] p-5">
            <p className="cv-kicker text-[#A6633C]">Pipeline</p>
            <label className="block">
              <span className="cv-sans uppercase tracking-[0.12em] text-[0.66rem] text-[#1F1B16]/60">Status</span>
              <select data-testid="crm-drawer-status" className="cv-input mt-1.5" value={status}
                onChange={(e) => setStatus(e.target.value)}>
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="cv-sans uppercase tracking-[0.12em] text-[0.66rem] text-[#1F1B16]/60">Last contacted</span>
              <div className="mt-1.5 flex items-center gap-2">
                <input data-testid="crm-drawer-last-contacted" type="date" className="cv-input"
                  value={lastContacted} onChange={(e) => setLastContacted(e.target.value)} />
                <button type="button" data-testid="crm-drawer-today"
                  onClick={() => setLastContacted(new Date().toISOString().slice(0, 10))}
                  className="cv-btn-secondary text-xs py-2 whitespace-nowrap">Today</button>
              </div>
            </label>
            <label className="block">
              <span className="cv-sans uppercase tracking-[0.12em] text-[0.66rem] text-[#1F1B16]/60">Notes</span>
              <textarea data-testid="crm-drawer-notes" rows={4} className="cv-input mt-1.5 resize-y"
                placeholder="Private sales notes..." value={notes}
                onChange={(e) => setNotes(e.target.value)} />
            </label>
            <button data-testid="crm-drawer-save" onClick={save} disabled={saving}
              className="cv-btn-primary inline-flex items-center gap-2 disabled:opacity-60">
              <Check size={14} /> {saving ? "Saving..." : "Save"}
            </button>
          </div>

          {/* Activity timeline */}
          <div className="mt-7">
            <p className="cv-kicker">Activity ({(contact.interactions || []).length})</p>
            <div className="mt-3 space-y-3">
              {(contact.interactions || []).map((it) => (
                <div key={it.id} data-testid="crm-drawer-interaction"
                  className="border-l-2 border-[rgba(28,27,25,0.15)] pl-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className={SOURCE_TAG}>{it.source}</span>
                    <span className="cv-body text-[0.66rem] text-[#9A938A]">{fmtDate(it.created_at)}</span>
                  </div>
                  {it.detail && <p className="cv-body text-xs text-[#1F1B16]/75 mt-1.5">{it.detail}</p>}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ListSkeleton() {
  return (
    <div className="border-t border-[rgba(28,27,25,0.12)]">
      {[0, 1, 2, 3, 4].map((i) => (
        <div key={i} className="py-4 border-b border-[rgba(28,27,25,0.08)] flex items-center gap-4">
          <div className="h-3 w-40 bg-[rgba(28,27,25,0.08)] rounded animate-pulse" />
          <div className="h-3 w-24 bg-[rgba(28,27,25,0.06)] rounded animate-pulse" />
          <div className="h-3 w-16 bg-[rgba(28,27,25,0.06)] rounded animate-pulse ml-auto" />
        </div>
      ))}
    </div>
  );
}
